
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiUser, FiMail, FiLock, FiPhone } from 'react-icons/fi';
import { authAPI } from '../services/api';
import Navbar from '../components/Navbar';

const Register = () => {
  // Nouvelles couleurs de la charte graphique
  const primaryColor = 'text-yellow-400';
  const primaryBg = 'bg-yellow-400';
  const primaryHover = 'hover:bg-yellow-500';
  const primaryFocus = 'focus:ring-yellow-400';
  const primaryBorder = 'border-yellow-400';
  
  const secondaryColor = 'text-gray-400';
  const secondaryBg = 'bg-gray-100';
  const secondaryHover = 'hover:bg-gray-200';
  const secondaryBorder = 'border-gray-300';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: '',
    role_id: '',
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Effacer l'erreur du champ modifié
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Format d\'email invalide';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Le téléphone est requis';
    }
    
    if (!formData.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Minimum 6 caractères';
    }
    
    if (formData.password !== formData.password_confirmation) {
      newErrors.password_confirmation = 'Les mots de passe ne correspondent pas';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrors({});
    
    try {
      formData.role_id = 3; // Rôle par défaut pour les utilisateurs
      console.log('Inscription:', formData);
      const userResponse = await authAPI.register(formData);
      
      alert('Inscription réussie !');
      navigate('/login');
    } catch (error) {
      console.error('Erreur inscription:', error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        alert('Erreur lors de l\'inscription. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen ${secondaryBg} bg-gradient-to-br from-gray-50 to-gray-100`}>
      <Navbar />
      
      <div className="max-w-lg mx-auto px-4 py-8 sm:py-12">
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 transition-all duration-300 hover:shadow-2xl">
          {/* En-tête avec logo ou icône */}
          <div className="flex flex-col items-center mb-8">
            <div className={`w-16 h-16 ${primaryBg} rounded-full flex items-center justify-center mb-4`}>
              <FiUser className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2">
              Créer un compte
            </h2>
            <p className={`text-sm ${secondaryColor}`}>
              Rejoignez notre communauté
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Nom complet */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Nom complet
              </label>
              <div className="relative">
                <FiUser className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${secondaryColor}`} />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={`w-full pl-12 pr-4 py-3 ${secondaryBorder} border rounded-xl focus:ring-2 ${primaryFocus} focus:border-transparent transition-all duration-200 ${
                    errors.name ? 'border-red-300' : ''
                  }`}
                  placeholder="Votre nom complet"
                />
              </div>
              {errors.name && (
                <p className="text-red-500 text-sm">{errors.name}</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <div className="relative">
                <FiMail className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${secondaryColor}`} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={`w-full pl-12 pr-4 py-3 ${secondaryBorder} border rounded-xl focus:ring-2 ${primaryFocus} focus:border-transparent transition-all duration-200 ${
                    errors.email ? 'border-red-300' : ''
                  }`}
                  placeholder="votre@email.com"
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-sm">{errors.email}</p>
              )}
            </div>

            {/* Téléphone */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Téléphone
              </label>
              <div className="relative">
                <FiPhone className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${secondaryColor}`} />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className={`w-full pl-12 pr-4 py-3 ${secondaryBorder} border rounded-xl focus:ring-2 ${primaryFocus} focus:border-transparent transition-all duration-200 ${
                    errors.phone ? 'border-red-300' : ''
                  }`}
                  placeholder="+237 XXX XXX XXX"
                />
              </div>
              {errors.phone && (
                <p className="text-red-500 text-sm">{errors.phone}</p>
              )}
            </div>

            {/* Mot de passe */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Mot de passe
              </label>
              <div className="relative">
                <FiLock className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${secondaryColor}`} />
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className={`w-full pl-12 pr-4 py-3 ${secondaryBorder} border rounded-xl focus:ring-2 ${primaryFocus} focus:border-transparent transition-all duration-200 ${
                    errors.password ? 'border-red-300' : ''
                  }`}
                  placeholder="Minimum 6 caractères"
                />
              </div>
              {errors.password && (
                <p className="text-red-500 text-sm">{errors.password}</p>
              )}
            </div>

            {/* Confirmation mot de passe */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Confirmer le mot de passe
              </label>
              <div className="relative">
                <FiLock className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${secondaryColor}`} />
                <input
                  type="password"
                  name="password_confirmation"
                  value={formData.password_confirmation}
                  onChange={handleChange}
                  required
                  className={`w-full pl-12 pr-4 py-3 ${secondaryBorder} border rounded-xl focus:ring-2 ${primaryFocus} focus:border-transparent transition-all duration-200 ${
                    errors.password_confirmation ? 'border-red-300' : ''
                  }`}
                  placeholder="Confirmez votre mot de passe"
                />
              </div>
              {errors.password_confirmation && (
                <p className="text-red-500 text-sm">{errors.password_confirmation}</p>
              )}
            </div>

            {/* Bouton d'inscription */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full px-6 py-4 ${primaryBg} ${primaryHover} text-white rounded-xl font-semibold transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-lg hover:shadow-xl`}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Inscription en cours...
                </div>
              ) : (
                'S\'inscrire'
              )}
            </button>
          </form>

          {/* Lien vers login */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <p className="text-gray-600">
              Déjà un compte ?{' '}
              <Link 
                to="/login" 
                className={`${primaryColor} hover:underline font-semibold transition-colors duration-200`}
              >
                Se connecter
              </Link>
            </p>
          </div>

          {/* Conditions d'utilisation */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              En vous inscrivant, vous acceptez nos{' '}
              <a href="#" className={`${primaryColor} hover:underline`}>
                Conditions d'utilisation
              </a>{' '}
              et notre{' '}
              <a href="#" className={`${primaryColor} hover:underline`}>
                Politique de confidentialité
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;